/**
 * Analytics Utilities
 *
 * Wrapper around react-ga4 for type-safe event tracking
 */

import ReactGA from 'react-ga4';
import type { Metric } from 'web-vitals';
import { GA4_MEASUREMENT_ID, analyticsConfig, isAnalyticsConfigured } from '../config/analytics';

/**
 * Initialize Google Analytics
 * Call this once at app startup
 */
export const initializeAnalytics = (): void => {
  if (!isAnalyticsConfigured()) {
    if (analyticsConfig.debug) {
      console.warn('[Analytics] GA4 Measurement ID not configured. See ANALYTICS_SETUP.md');
    }
    return;
  }

  try {
    ReactGA.initialize(GA4_MEASUREMENT_ID, {
      gaOptions: analyticsConfig.gaOptions,
      gtagOptions: {
        debug_mode: analyticsConfig.debug,
      }
    });

    if (analyticsConfig.debug) {
      console.log('[Analytics] Initialized GA4:', GA4_MEASUREMENT_ID);
    }
  } catch (error) {
    console.error('[Analytics] Initialization failed:', error);
  }
};

/**
 * Track a page view
 */
export const trackPageView = (path: string, title?: string): void => {
  if (!analyticsConfig.enabled || !isAnalyticsConfigured()) return;

  try {
    ReactGA.send({
      hitType: 'pageview',
      page: path,
      title: title || document.title,
    });

    if (analyticsConfig.debug) {
      console.log('[Analytics] Page view tracked:', { path, title });
    }
  } catch (error) {
    console.error('[Analytics] Page view tracking failed:', error);
  }
};

/**
 * Track a custom event
 */
export const trackEvent = (
  eventName: string,
  params?: Record<string, any>
): void => {
  if (!analyticsConfig.enabled || !isAnalyticsConfigured()) return;

  try {
    ReactGA.event(eventName, params);

    if (analyticsConfig.debug) {
      console.log('[Analytics] Event tracked:', eventName, params);
    }
  } catch (error) {
    console.error('[Analytics] Event tracking failed:', error);
  }
};

// Predefined event helpers for type safety

/**
 * Track CTA button clicks
 */
export const trackCTAClick = (buttonLocation: string): void => {
  trackEvent('cta_click', {
    button_location: buttonLocation,
  });
};

/**
 * Track App Store link clicks
 */
export const trackAppStoreClick = (platform: 'ios' = 'ios'): void => {
  trackEvent('app_store_click', {
    platform,
  });
};

/**
 * Track external link clicks
 */
export const trackExternalLink = (url: string, linkText: string): void => {
  trackEvent('external_link_click', {
    url,
    link_text: linkText,
  });
};

/**
 * Track web vitals metrics
 */
export const trackWebVital = (metric: Metric & { rating?: string }): void => {
  if (!analyticsConfig.enabled || !isAnalyticsConfigured()) return;

  try {
    ReactGA.event('web_vitals', {
      event_category: 'Web Vitals',
      event_label: metric.name,
      value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      metric_name: metric.name,
      metric_value: metric.value,
      ...(metric.rating && { metric_rating: metric.rating }),
      metric_delta: metric.delta,
      non_interaction: true, // Don't affect bounce rate
    });

    if (analyticsConfig.debug) {
      console.log('[Analytics] Web Vital tracked:', metric);
    }
  } catch (error) {
    console.error('[Analytics] Web Vital tracking failed:', error);
  }
};
